import React, { useRef, useState } from 'react';
import { Mail, ArrowRight, AlertCircle, Eye, EyeOff, Shield, ArrowLeft, CheckCircle, UserPlus, Star, Users, Globe, Calculator, ChevronDown, Smartphone } from 'lucide-react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, confirmPasswordReset } from 'firebase/auth';
import { auth } from './firebase';
import { getUserByEmail } from './services/db';
import { trackEvent } from './utils/analytics';
import PortalOverview from './PortalOverview';

// Move components outside to prevent re-creation on every render
const FloatingCard = ({ children, style = {} }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    ...style
  }}>
    {children}
  </div>
);

// Single iPhone with frame image
const IPhone = ({ screenshot = '/app-screenshot.png', style = {} }) => (
  <div style={{
    position: 'relative',
    width: '260px',
    height: '530px',
    ...style
  }}>
    {/* App screenshot behind the frame */}
    <div style={{
      position: 'absolute',
      top: '1.2%',
      left: '3.5%',
      width: '93%',
      height: '97.5%',
      borderRadius: '38px',
      overflow: 'hidden',
      zIndex: 1,
    }}>
      <img
        src={screenshot}
        alt="MathZoo Junior App"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top center',
        }}
      />
    </div>
    {/* iPhone frame on top */}
    <img
      src="/iphone-frame.png"
      alt="iPhone"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        zIndex: 2,
      }}
    />
  </div>
);

// Dual iPhone mockup using frame image
const PhoneMockup = () => (
  <div style={{
    position: 'relative',
    width: '460px',
    height: '580px',
  }}>
    {/* Back phone (right, slightly behind) */}
    <div style={{
      position: 'absolute',
      right: '0',
      top: '50px',
      zIndex: 1,
      filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.3))',
    }}>
      <IPhone screenshot="/app-screenshot-2.png" />
    </div>
    {/* Front phone (left, in front) */}
    <div style={{
      position: 'absolute',
      left: '0',
      top: '0',
      zIndex: 2,
      filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.25))',
    }}>
      <IPhone screenshot="/app-screenshot.png" />
    </div>
  </div>
);

const LoginPage = ({ onLogin, appColors }) => {
  // Detect Firebase password reset link: ?mode=resetPassword&oobCode=...
  const _urlParams = new URLSearchParams(window.location.search);
  const _oobCode = _urlParams.get('oobCode') || '';
  const _urlMode = _urlParams.get('mode') || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile] = useState(window.innerWidth <= 768);
  // Views: 'email' | 'password' | 'createPassword' | 'forgotPassword' | 'resetPassword'
  const [view, setView] = useState(_urlMode === 'resetPassword' && _oobCode ? 'resetPassword' : 'email');
  // Store user data after email check
  const [foundUserData, setFoundUserData] = useState(null);
  const authCardRef = useRef(null);

  const scrollToParentAccount = () => {
    trackEvent('overview_parent_account_click');
    authCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    authCardRef.current?.querySelector('input')?.focus({ preventScroll: true });
  };

  const scrollToOverview = () => {
    trackEvent('overview_scroll_click');
    document.getElementById('portal-overview')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to find user data by email using emailIndex
  const findUserByEmail = async (userEmail) => {
    try {
      return await getUserByEmail(userEmail);
    } catch (err) {
      console.error('Error finding user:', err);
      return null;
    }
  };

  // Helper function to get Firebase auth error message
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-credential':
        return 'Incorrect password. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed. Please try again.';
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled.';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  // Reset to email entry
  const resetToEmail = () => {
    setView('email');
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFoundUserData(null);
  };

  // Handle email submission - check if user exists in DB
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Check if email exists in our database (emailIndex/users)
      const userResult = await findUserByEmail(email);

      if (!userResult) {
        // Email not found in database - not an Expert user
        setError('This email is not registered. Please upgrade to Expert mode in the MathZoo app first, then return here to set up your dashboard access.');
        setLoading(false);
        return;
      }

      // Store user data for later use
      setFoundUserData(userResult);

      // Step 2: Check if user has a Firebase Auth account via the Identity Toolkit REST API.
      // We use createAuthUri instead of fetchSignInMethodsForEmail because the JS SDK method
      // is affected by Email Enumeration Protection even when the toggle is off, while the
      // REST endpoint reliably returns registered: true/false.
      try {
        const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
        const authCheckResponse = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:createAuthUri?key=${apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: email, continueUri: window.location.origin }),
          }
        );
        const authCheckData = await authCheckResponse.json();
        console.log('Auth check result:', authCheckData);

        if (authCheckData.registered === true) {
          setView('password');
        } else {
          setView('createPassword');
        }
      } catch (authCheckError) {
        console.error('Firebase Auth check error:', authCheckError);
        // Network failure — default to password view as the safer fallback
        setView('password');
      }
    } catch (err) {
      console.error('Email check error:', err);
      setError(getErrorMessage(err.code));
    }

    setLoading(false);
  };

  // Handle password login for existing users
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      console.log('Login successful:', firebaseUser.email);
      trackEvent('login', { method: 'email' });

      // Use stored user data or fetch again
      if (foundUserData) {
        onLogin(foundUserData.userId, foundUserData.userData, firebaseUser);
      } else {
        const userResult = await findUserByEmail(firebaseUser.email);
        if (userResult) {
          onLogin(userResult.userId, userResult.userData, firebaseUser);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      // Check if this is a wrong password (user exists) vs user not found (need to create)
      if (err.code === 'auth/wrong-password') {
        // Definitely wrong password, account exists
        setError(getErrorMessage(err.code));
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        // User not found in Firebase Auth - redirect to create password view
        setPassword('');
        setError('');
        setView('createPassword');
      } else {
        setError(getErrorMessage(err.code));
      }
    }

    setLoading(false);
  };

  // Handle password creation for new users
  const handleCreatePassword = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password should be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      console.log('Account created:', firebaseUser.email);
      trackEvent('sign_up', { method: 'email' });

      // Use stored user data
      if (foundUserData) {
        onLogin(foundUserData.userId, foundUserData.userData, firebaseUser);
      }
    } catch (err) {
      console.error('Create account error:', err);
      if (err.code === 'auth/email-already-in-use') {
        // Account exists, switch to password view
        setError('An account already exists. Please enter your password.');
        setView('password');
      } else {
        setError(getErrorMessage(err.code));
      }
    }

    setLoading(false);
  };

  // Handle forgot password — calls our Vercel endpoint which sends a
  // branded SendGrid email instead of Firebase's default plain-text one.
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch('/api/send-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.status === 429) {
        setError('Too many reset attempts. Please try again later.');
      } else {
        trackEvent('password_reset_request');
        // Always show success — we never reveal whether an email exists
        setSuccess('Password reset email sent! Check your inbox (and Spam/Junk folder).');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      // Network failure — fall back to success message to avoid confusion
      setSuccess('Password reset email sent! Check your inbox (and Spam/Junk folder).');
    }

    setLoading(false);
  };

  // Handle the actual password reset (oobCode from email link)
  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, _oobCode, password);
      setSuccess('Your password has been reset successfully!');
      // Clear oobCode from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error('Confirm reset error:', err);
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or already used. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${appColors.background} 0%, #f8f4e6 100%)`,
      backgroundAttachment: 'fixed',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      overflowX: 'hidden',
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'fixed',
        top: '-20%',
        right: '-10%',
        width: '500px',
        height: '500px',
        background: `radial-gradient(circle, ${appColors.primary}20 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-15%',
        width: '600px',
        height: '600px',
        background: `radial-gradient(circle, ${appColors.accent}30 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      {/* Hero - header + sign in, fills the first screen */}
      <div style={{ minHeight: 'min(100vh, 1200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header - matching dashboard layout */}
      <header style={{
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        justifyContent: 'center',
        padding: isMobile ? '0 20px' : '0 48px',
      }}>
        <div style={{
          maxWidth: '1100px',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Logo - same size as dashboard */}
          <img
            src="/mathzoo-logo.png"
            alt="MathZoo Junior"
            style={{ height: isMobile ? '60px' : '100px', paddingTop: '10px', marginLeft: '-12px' }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* App Store badge - Apple style in green */}
            <button
              onClick={() => { trackEvent('app_store_click', { location: 'login_header' }); window.open('https://apps.apple.com/us/app/mathzoo-junior/id6757204107', '_blank'); }}
              style={{
                padding: isMobile ? '8px 10px' : '8px 16px 8px 12px',
                border: 'none',
                background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '0px' : '8px',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)',
                height: isMobile ? '36px' : '44px',
              }}
            >
              {/* Apple logo SVG */}
              <svg viewBox="0 0 384 512" width={isMobile ? '20' : '22'} height={isMobile ? '20' : '22'} fill="white">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              {!isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
                  <span style={{ fontSize: '9px', fontWeight: '400', letterSpacing: '0.5px' }}>Download on the</span>
                  <span style={{ fontSize: '18px', fontWeight: '600', letterSpacing: '-0.3px', marginTop: '1px' }}>App Store</span>
                </div>
              )}
              {isMobile && (
                <span style={{ fontSize: '11px', fontWeight: '600', marginLeft: '4px' }}>App Store</span>
              )}
            </button>

            {/* Contact Us button — uses <a> with mailto so iOS Safari opens Mail correctly */}
            <a
              href="mailto:office@techconsulting.nl?subject=MathZoo%20Portal%20Request"
              style={{
                padding: isMobile ? '8px 10px' : '8px 16px 8px 12px',
                background: appColors.textPrimary,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                height: isMobile ? '36px' : '44px',
                textDecoration: 'none',
                boxSizing: 'border-box',
              }}
            >
              <Mail size={isMobile ? 18 : 20} color="white" />
              {!isMobile && <span style={{ fontSize: '16px', fontWeight: '600' }}>Contact Us</span>}
              {isMobile && <span style={{ fontSize: '11px', fontWeight: '600' }}>Contact</span>}
            </a>
          </div>
        </div>
      </header>

      {/* Main content - optimized for 1200px height */}
      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '16px 20px' : '24px 48px',
        position: 'relative',
        zIndex: 10,
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          gap: isMobile ? '24px' : '40px',
          maxWidth: '1100px',
          width: '100%',
        }}>
          {/* Left side - Hero text and login form */}
          <div style={{
            flex: 1,
            maxWidth: isMobile ? '100%' : '520px',
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '999px',
              background: 'rgba(255, 255, 255, 0.7)',
              border: `1px solid ${appColors.primary}66`,
              color: appColors.primary,
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '14px',
            }}>
              <Smartphone size={14} />
              Parent portal for the MathZoo Junior iPhone app
            </div>
            <h1 style={{
              fontSize: isMobile ? '36px' : '58px',
              fontWeight: 'bold',
              color: appColors.textPrimary,
              lineHeight: 1.1,
              margin: '0 0 16px 0',
            }}>
              Track your child's{' '}
              <span style={{ color: appColors.primary }}>math journey</span>
            </h1>
            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: appColors.textSecondary,
              marginBottom: '24px',
              lineHeight: 1.6,
            }}>
              Your child practises in the app. Every answer syncs here, so you can see what's working, where they need help, and exactly what to do next.
            </p>

            {/* Auth form card */}
            <div ref={authCardRef}>
            <FloatingCard style={{ padding: isMobile ? '20px' : '24px' }}>

              {/* EMAIL ENTRY VIEW */}
              {view === 'email' && (
                <>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: appColors.textPrimary,
                    margin: '0 0 4px 0',
                  }}>
                    Parent Dashboard
                  </h2>
                  <p style={{
                    fontSize: '13px',
                    color: appColors.textSecondary,
                    margin: '0 0 16px 0',
                  }}>
                    Enter your registered email (for iOS app Expert mode users only)
                  </p>

                  <form onSubmit={handleEmailSubmit}>
                    {/* Email input */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: appColors.textPrimary,
                        marginBottom: '6px',
                      }}>
                        Email Address
                      </label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Mail size={18} color={appColors.textSecondary} style={{ position: 'absolute', left: '14px' }} />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="parent@example.com"
                          required
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '12px 14px 12px 44px',
                            fontSize: '15px',
                            border: `2px solid ${appColors.separatorBrown}`,
                            borderRadius: '10px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.target.style.borderColor = appColors.primary}
                          onBlur={(e) => e.target.style.borderColor = appColors.separatorBrown}
                        />
                      </div>
                    </div>

                    {/* Error message */}
                    {error && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '10px 14px',
                        background: '#FEE2E2',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        <AlertCircle size={16} color="#DC2626" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ color: '#DC2626', fontSize: '13px' }}>{error}</span>
                      </div>
                    )}

                    {/* Continue button */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        background: loading ? appColors.textSecondary : `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '14px 20px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {loading ? 'Checking...' : <><span>Continue</span><ArrowRight size={18} /></>}
                    </button>
                  </form>

                </>
              )}

              {/* PASSWORD ENTRY VIEW (existing user) */}
              {view === 'password' && (
                <>
                  <button
                    type="button"
                    onClick={resetToEmail}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'none',
                      border: 'none',
                      color: appColors.textSecondary,
                      fontSize: '13px',
                      cursor: 'pointer',
                      padding: 0,
                      marginBottom: '12px',
                    }}
                  >
                    <ArrowLeft size={16} />
                    Change email
                  </button>

                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: appColors.textPrimary,
                    margin: '0 0 4px 0',
                  }}>
                    Welcome back!
                  </h2>
                  <p style={{
                    fontSize: '13px',
                    color: appColors.textSecondary,
                    margin: '0 0 16px 0',
                  }}>
                    Enter your password for <strong>{email}</strong>
                  </p>

                  <form onSubmit={handlePasswordSubmit}>
                    {/* Password input */}
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: appColors.textPrimary,
                        marginBottom: '6px',
                      }}>
                        Password
                      </label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Shield size={18} color={appColors.textSecondary} style={{ position: 'absolute', left: '14px' }} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '12px 44px 12px 44px',
                            fontSize: '15px',
                            border: `2px solid ${appColors.separatorBrown}`,
                            borderRadius: '10px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.target.style.borderColor = appColors.primary}
                          onBlur={(e) => e.target.style.borderColor = appColors.separatorBrown}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ position: 'absolute', right: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          {showPassword ? <EyeOff size={18} color={appColors.textSecondary} /> : <Eye size={18} color={appColors.textSecondary} />}
                        </button>
                      </div>
                    </div>

                    {/* Forgot password link */}
                    <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                      <button
                        type="button"
                        onClick={() => { setView('forgotPassword'); setError(''); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: appColors.primary,
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>

                    {/* Error message */}
                    {error && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: '#FEE2E2',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        <AlertCircle size={16} color="#DC2626" />
                        <span style={{ color: '#DC2626', fontSize: '13px' }}>{error}</span>
                      </div>
                    )}

                    {/* Sign In button */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        background: loading ? appColors.textSecondary : `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '14px 20px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {loading ? 'Signing in...' : <><span>Sign In</span><ArrowRight size={18} /></>}
                    </button>
                  </form>
                </>
              )}

              {/* CREATE PASSWORD VIEW (new user) */}
              {view === 'createPassword' && (
                <>
                  <button
                    type="button"
                    onClick={resetToEmail}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'none',
                      border: 'none',
                      color: appColors.textSecondary,
                      fontSize: '13px',
                      cursor: 'pointer',
                      padding: 0,
                      marginBottom: '12px',
                    }}
                  >
                    <ArrowLeft size={16} />
                    Change email
                  </button>

                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: appColors.textPrimary,
                    margin: '0 0 4px 0',
                  }}>
                    Create your password
                  </h2>
                  <p style={{
                    fontSize: '13px',
                    color: appColors.textSecondary,
                    margin: '0 0 16px 0',
                  }}>
                    Set up dashboard access for <strong>{email}</strong>
                  </p>

                  <form onSubmit={handleCreatePassword}>
                    {/* Password input */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: appColors.textPrimary,
                        marginBottom: '6px',
                      }}>
                        Password
                      </label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Shield size={18} color={appColors.textSecondary} style={{ position: 'absolute', left: '14px' }} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Create a password (min 6 chars)"
                          required
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '12px 44px 12px 44px',
                            fontSize: '15px',
                            border: `2px solid ${appColors.separatorBrown}`,
                            borderRadius: '10px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.target.style.borderColor = appColors.primary}
                          onBlur={(e) => e.target.style.borderColor = appColors.separatorBrown}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ position: 'absolute', right: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          {showPassword ? <EyeOff size={18} color={appColors.textSecondary} /> : <Eye size={18} color={appColors.textSecondary} />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password input */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: appColors.textPrimary,
                        marginBottom: '6px',
                      }}>
                        Confirm Password
                      </label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Shield size={18} color={appColors.textSecondary} style={{ position: 'absolute', left: '14px' }} />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          required
                          style={{
                            width: '100%',
                            padding: '12px 44px 12px 44px',
                            fontSize: '15px',
                            border: `2px solid ${appColors.separatorBrown}`,
                            borderRadius: '10px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.target.style.borderColor = appColors.primary}
                          onBlur={(e) => e.target.style.borderColor = appColors.separatorBrown}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{ position: 'absolute', right: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          {showConfirmPassword ? <EyeOff size={18} color={appColors.textSecondary} /> : <Eye size={18} color={appColors.textSecondary} />}
                        </button>
                      </div>
                    </div>

                    {/* Error message */}
                    {error && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: '#FEE2E2',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        <AlertCircle size={16} color="#DC2626" />
                        <span style={{ color: '#DC2626', fontSize: '13px' }}>{error}</span>
                      </div>
                    )}

                    {/* Create Account button */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        background: loading ? appColors.textSecondary : `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '14px 20px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {loading ? 'Creating...' : <><span>Create Account</span><UserPlus size={18} /></>}
                    </button>
                  </form>
                </>
              )}

              {/* FORGOT PASSWORD VIEW */}
              {view === 'forgotPassword' && (
                <>
                  <button
                    type="button"
                    onClick={() => { setView('password'); setError(''); setSuccess(''); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'none',
                      border: 'none',
                      color: appColors.textSecondary,
                      fontSize: '13px',
                      cursor: 'pointer',
                      padding: 0,
                      marginBottom: '12px',
                    }}
                  >
                    <ArrowLeft size={16} />
                    Back to login
                  </button>

                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: appColors.textPrimary,
                    margin: '0 0 4px 0',
                  }}>
                    Reset Password
                  </h2>
                  <p style={{
                    fontSize: '13px',
                    color: appColors.textSecondary,
                    margin: '0 0 16px 0',
                  }}>
                    We'll send a reset link to <strong>{email}</strong>
                  </p>

                  <form onSubmit={handleForgotPassword}>
                    {/* Error message */}
                    {error && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: '#FEE2E2',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        <AlertCircle size={16} color="#DC2626" />
                        <span style={{ color: '#DC2626', fontSize: '13px' }}>{error}</span>
                      </div>
                    )}

                    {/* Success message */}
                    {success && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: `${appColors.primary}18`,
                        border: `1px solid ${appColors.primary}50`,
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        <CheckCircle size={16} color={appColors.primary} />
                        <span style={{ color: appColors.primary, fontSize: '13px' }}>{success}</span>
                      </div>
                    )}

                    {/* Send Reset Link button — hidden after success */}
                    {!success && (
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          background: loading ? appColors.textSecondary : `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '14px 20px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)',
                          transition: 'all 0.2s',
                        }}
                      >
                        {loading ? 'Sending...' : <><span>Send Reset Link</span><Mail size={18} /></>}
                      </button>
                    )}

                    {/* Post-send: resend option */}
                    {success && (
                      <div style={{ textAlign: 'center', marginTop: '4px' }}>
                        <span style={{ fontSize: '13px', color: appColors.textSecondary }}>
                          Didn't receive it?{' '}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setSuccess(''); setError(''); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: appColors.primary,
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '0',
                            textDecoration: 'underline',
                          }}
                        >
                          Resend email
                        </button>
                      </div>
                    )}
                  </form>
                </>
              )}

              {/* ── Reset Password view (oobCode from email link) ── */}
              {/* Reuses exact same UI as createPassword — just different title/subtitle/handler */}
              {view === 'resetPassword' && (
                <>
                  <button
                    type="button"
                    onClick={() => { setView('email'); setError(''); setSuccess(''); setPassword(''); setConfirmPassword(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      background: 'none', border: 'none', color: appColors.textSecondary,
                      fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '12px',
                    }}
                  >
                    <ArrowLeft size={16} />
                    Back to login
                  </button>

                  <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: appColors.textPrimary, margin: '0 0 4px 0' }}>
                    Choose a new password
                  </h2>
                  <p style={{ fontSize: '13px', color: appColors.textSecondary, margin: '0 0 16px 0' }}>
                    Set a new password for your MathZoo Dashboard account.
                  </p>

                  {/* Success state */}
                  {success ? (
                    <>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 14px',
                        background: `${appColors.primary}18`,
                        border: `1px solid ${appColors.primary}50`,
                        borderRadius: '8px', marginBottom: '16px',
                      }}>
                        <CheckCircle size={16} color={appColors.primary} />
                        <span style={{ color: appColors.primary, fontSize: '13px' }}>{success}</span>
                      </div>
                      <button
                        onClick={() => { setSuccess(''); setError(''); setPassword(''); setConfirmPassword(''); setView('email'); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                          color: 'white', border: 'none', borderRadius: '10px',
                          padding: '14px 20px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)',
                        }}
                      >
                        <span>Sign In</span><ArrowRight size={18} />
                      </button>
                    </>
                  ) : (
                    <form onSubmit={handleConfirmPasswordReset}>
                      {/* Password input — identical to createPassword */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: appColors.textPrimary, marginBottom: '6px' }}>
                          Password
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <Shield size={18} color={appColors.textSecondary} style={{ position: 'absolute', left: '14px' }} />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="New password (min 6 chars)"
                            required
                            autoFocus
                            style={{
                              width: '100%', padding: '12px 44px 12px 44px', fontSize: '15px',
                              border: `2px solid ${appColors.separatorBrown}`,
                              borderRadius: '10px', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
                            }}
                            onFocus={(e) => e.target.style.borderColor = appColors.primary}
                            onBlur={(e) => e.target.style.borderColor = appColors.separatorBrown}
                          />
                          <button type="button" onClick={() => setShowPassword(!showPassword)}
                            style={{ position: 'absolute', right: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            {showPassword ? <EyeOff size={18} color={appColors.textSecondary} /> : <Eye size={18} color={appColors.textSecondary} />}
                          </button>
                        </div>
                      </div>

                      {/* Confirm Password — identical to createPassword */}
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: appColors.textPrimary, marginBottom: '6px' }}>
                          Confirm Password
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <Shield size={18} color={appColors.textSecondary} style={{ position: 'absolute', left: '14px' }} />
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your password"
                            required
                            style={{
                              width: '100%', padding: '12px 44px 12px 44px', fontSize: '15px',
                              border: `2px solid ${appColors.separatorBrown}`,
                              borderRadius: '10px', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
                            }}
                            onFocus={(e) => e.target.style.borderColor = appColors.primary}
                            onBlur={(e) => e.target.style.borderColor = appColors.separatorBrown}
                          />
                          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{ position: 'absolute', right: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            {showConfirmPassword ? <EyeOff size={18} color={appColors.textSecondary} /> : <Eye size={18} color={appColors.textSecondary} />}
                          </button>
                        </div>
                      </div>

                      {/* Error */}
                      {error && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '10px 14px', background: '#FEE2E2', borderRadius: '8px', marginBottom: '12px',
                        }}>
                          <AlertCircle size={16} color="#DC2626" />
                          <span style={{ color: '#DC2626', fontSize: '13px' }}>{error}</span>
                        </div>
                      )}

                      {/* Submit button */}
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          background: loading ? appColors.textSecondary : `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.secondary} 100%)`,
                          color: 'white', border: 'none', borderRadius: '10px',
                          padding: '14px 20px', fontSize: '15px', fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          boxShadow: '0 4px 12px rgba(129, 165, 80, 0.3)', transition: 'all 0.2s',
                        }}
                      >
                        {loading ? 'Saving...' : <><span>Save New Password</span><ArrowRight size={18} /></>}
                      </button>
                    </form>
                  )}
                </>
              )}

            </FloatingCard>
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '10px',
              marginTop: '20px',
            }}>
              {[
                { value: '4.9', label: 'App Review', icon: Star },
                { value: '500+', label: 'App Users', icon: Users },
                { value: '6', label: 'Languages', icon: Globe },
                { value: '5x', label: 'More Math', icon: Calculator },
              ].map((stat, idx) => (
                <div key={idx} style={{
                  background: `${appColors.primary}18`,
                  border: `1px solid ${appColors.primary}B3`,
                  borderRadius: '16px',
                  padding: '12px 8px',
                  textAlign: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    {stat.icon && <stat.icon size={14} color={appColors.primary} />}
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: appColors.textPrimary }}>{stat.value}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: appColors.textSecondary, marginTop: '2px' }}>{stat.label}</div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={scrollToOverview}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                margin: '18px auto 0',
                background: 'none',
                border: 'none',
                color: appColors.primary,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
            >
              See what the parent portal does
              <ChevronDown size={18} />
            </button>
          </div>

          {/* Right side - Phone mockup with real hand image */}
          {!isMobile && (
            <div style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              maxWidth: '600px',
            }}>
              <PhoneMockup />
            </div>
          )}
        </div>
      </main>
      </div>

      <PortalOverview appColors={appColors} onParentAccountClick={scrollToParentAccount} />

      {/* Footer */}
      <footer style={{
        padding: '12px 48px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10,
      }}>
        <p style={{ fontSize: '12px', color: appColors.textSecondary, margin: 0 }}>
          &copy; 2026 MathZoo Junior. All rights reserved.
          {' · '}
          <a
            href="/privacy"
            style={{ color: appColors.primary, textDecoration: 'none', fontWeight: '500' }}
          >
            Privacy Policy
          </a>
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
